/**
 * HTTP transport for view-level interactions — the `/action` endpoint accepts
 * both module and view-interaction payloads, discriminated on `scope`. View
 * interactions are routed here so the same fetch / abort / response parsing
 * machinery (and therefore the same error contract) is shared with
 * {@link submitModuleAction}.
 */

import type {
  VersionString,
  ViewID,
  ViewInteractionActionPayload,
} from "@bestowinc/tinkertoy-core";

import { withInstrumentedAction } from "./action-observability";
import {
  releaseActionAbortSlot,
  supersedeActionRequest,
  viewInteractionScope,
} from "./action-abort-registry";
import { actionUrl, currentRoute } from "./action-request";
import { TinkertoyActionError } from "./action-errors";
import { parseActionResponseBody } from "./parse-action-response";

export type SubmitViewInteractionArgs = {
  apiBaseUrl: string;
  /** Identity of the view whose interaction is being invoked. */
  viewId: ViewID;
  viewVersion: VersionString;
  /** Interaction key on the view's `interactions` map. */
  actionName: string;
  /** The interaction's input payload — validated server-side by the action's schema. */
  data: unknown;
};

/**
 * POST a {@link ViewInteractionActionPayload} to the Tinkertoy server `/action`
 * endpoint and return the parsed `data` on success.
 *
 * Behaviour matches {@link submitModuleAction} on every dimension that's not
 * scope-specific:
 * - In-flight requests for the same `(viewId, actionName)` are superseded so
 *   the latest interaction wins (the abort registry namespaces by these two
 *   keys; using `viewId` here keeps view interactions from clobbering module
 *   actions whose first arg is a `moduleId`).
 * - Every non-abort failure surfaces as a {@link TinkertoyActionError} with
 *   the appropriate `code` (validation, handler, pipeline, or HTTP status).
 *
 * @throws {TinkertoyActionError}
 * @throws {DOMException} `AbortError` when superseded or cancelled
 */
export async function submitViewInteraction({
  apiBaseUrl,
  viewId,
  viewVersion,
  actionName,
  data,
}: SubmitViewInteractionArgs): Promise<unknown> {
  return withInstrumentedAction(
    {
      actionScope: "view_interaction",
      actionName,
      viewId,
      viewVersion,
    },
    () =>
      submitViewInteractionInner({
        apiBaseUrl,
        viewId,
        viewVersion,
        actionName,
        data,
      }),
  );
}

async function submitViewInteractionInner({
  apiBaseUrl,
  viewId,
  viewVersion,
  actionName,
  data,
}: SubmitViewInteractionArgs): Promise<unknown> {
  const scope = viewInteractionScope(viewId);
  const signal = supersedeActionRequest(scope, actionName);

  try {
    const payload: ViewInteractionActionPayload = {
      scope: "view_interaction",
      viewId,
      viewVersion,
      actionName,
      data,
      route: currentRoute(),
    };

    const response = await fetch(actionUrl(apiBaseUrl), {
      method: "POST",
      signal,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }).catch((err: unknown) => {
      if (err instanceof DOMException && err.name === "AbortError") {
        throw err;
      }
      throw new TinkertoyActionError(
        err instanceof Error ? err.message : "Network request failed.",
        { code: "network_error" },
      );
    });

    let body: unknown;
    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
      try {
        body = await response.json();
      } catch {
        throw new TinkertoyActionError("Server returned invalid JSON.");
      }
    } else if (response.ok) {
      throw new TinkertoyActionError("Server returned a non-JSON response.");
    }

    if (body !== undefined) {
      return await parseActionResponseBody(body);
    }

    if (!response.ok) {
      throw new TinkertoyActionError(
        response.statusText ||
          `View interaction request failed (${response.status})`,
        { code: `HTTP_${response.status}` },
      );
    }

    throw new TinkertoyActionError("Empty view interaction response.");
  } finally {
    releaseActionAbortSlot(scope, actionName, signal);
  }
}
